import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import ImagePreview from '@/components/common/media/ImagePreview';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import { useListQueryParams } from '@/hooks/useListQueryParams';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  IBrandsResponse,
  IBrandsResponseList,
} from '@/interfaces/brandsResponse.interface';
import { ActionConfig, PageInfo } from '@/interfaces/common.interface';
import {
  filterOptions,
  GetFilterUrl,
  getStatusWarning,
  showToastNotification,
  statusOptions,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface BrandStates {
  isLoading: boolean;
  brands: IBrandsResponse[];
  pageInfo: PageInfo;
  selectedBrands: Set<string | number>;
  showDeleteModel: boolean;
  selectedBrandId: string | null;
  showUpdateModel: boolean;
  orderDir: string;
  statusToUpdate: string | null;
}

const Brands = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermissions();

  // ✅ URL-driven params
  const { params, setParams } = useListQueryParams({
    search: '',
    brandStatus: '',
    orderDir: 'newest',
  });

  const { page, limit, search, brandStatus, orderDir } = params;

  // ✅ Local state (UI + API response)
  const [states, setStates] = useState<BrandStates>({
    isLoading: false,
    brands: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit,
      page,
    },
    selectedBrands: new Set(),
    showDeleteModel: false,
    selectedBrandId: null,
    showUpdateModel: false,
    statusToUpdate: null,
    orderDir: 'newest',
  });

  const handleChangeState = (payload: Partial<BrandStates>) => {
    setStates((prev) => ({ ...prev, ...payload }));
  };

  // 🔹 Fetch brands
  const fetchBrandsList = async () => {
    try {
      handleChangeState({ isLoading: true });

      const query = GetFilterUrl({
        page,
        limit,
        status: brandStatus,
        search,
        sort: orderDir,
      });

      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}?${query}`;
      const res = await callApi<IBrandsResponseList>(PATH, { method: 'GET' });

      handleChangeState({
        brands: res.data,
        pageInfo: res.pageInfo,
        isLoading: false,
      });
    } catch (error) {
      ThrowError(error);
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchBrandsList();
  }, [page, limit, search, brandStatus, orderDir]);

  // 🔹 Status update
  const onStatusUpdate = async () => {
    if (!states.selectedBrandId || !states.statusToUpdate) return;

    try {
      handleChangeState({ isLoading: true });

      await callApi(
        `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/${states.selectedBrandId}`,
        { method: 'PATCH', data: { status: states.statusToUpdate } },
      );

      fetchBrandsList();
    } finally {
      handleChangeState({
        showUpdateModel: false,
        selectedBrandId: null,
        statusToUpdate: null,
        isLoading: false,
      });
    }
  };

  // 🔹 Delete brand
  const handleDeleteBrand = async () => {
    if (!states.selectedBrandId) return;

    try {
      handleChangeState({ isLoading: true });

      await callApi(
        `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/${states.selectedBrandId}`,
        { method: 'DELETE' },
      );

      showToastNotification('success', 'Brand deleted successfully');
      setParams({ page: 1 });
    } finally {
      handleChangeState({
        showDeleteModel: false,
        selectedBrandId: null,
        isLoading: false,
      });
    }
  };

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.brands);

  const columns: TableColumn<IBrandsResponse>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (item) => {
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${item?.media[0]?.url}`}
            text={item.name}
          />
        );
      },
      className: 'p-3',
    },
    {
      key: 'productCount',
      header: 'Product',
      className: 'p-3',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={statusOptions}
          onChange={(status) => {
            handleChangeState({
              showUpdateModel: true,
              selectedBrandId: item.id,
              statusToUpdate: status,
            });
          }}
          canEditVal="brand"
        />
      ),
    },
  ];

  const brandActionConfig: Record<string, ActionConfig<IBrandsResponse>> = {
    edit: {
      text: 'Edit',
      onClick: (brand) => navigate(`${RoutesLink.addBrands}/${brand.id}`),
      permission: () => canEdit('brand'),
    },
    delete: {
      text: 'Delete',
      onClick: (brand) =>
        handleChangeState({
          showDeleteModel: true,
          selectedBrandId: brand.id,
        }),
      permission: () => canDelete('brand'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(brandActionConfig)
        .filter((a) => a.permission())
        .map(({ permission, ...rest }) => rest),
    [canEdit, canDelete],
  );

  const sortOptions = [
    { value: 'az', label: 'A To Z' },
    { value: 'za', label: 'Z To A' },
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
  ];

  return (
    <Layout>
      <Card>
        <div className="flex justify-between items-start gap-3">
          <CommonFilter
            activeFilter={brandStatus}
            filters={filterOptions}
            inputPlaceHolder="Search for brands..."
            searchValue={search}
            onSearch={(text) => setParams({ search: text })}
            onChange={(filter) => setParams({ brandStatus: filter, page: 1 })}
            filterOptions={{
              values: sortOptions,
              onChange: (value) => setParams({ orderDir: value }),
            }}
          />

          {states.brands.length > 0 && (
            <ExportOption
              APIPath={`${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}/export/excel`}
              status={brandStatus}
            />
          )}
        </div>

        <CommonTable
          columns={columns}
          data={states.brands}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No brands found"
          loading={states.isLoading}
          actions={actions}
        />

        {states.pageInfo.totalPages > 1 && (
          <Pagination
            isLoading={states.isLoading}
            paginationData={states.pageInfo}
            onPageChange={(p) => setParams({ page: p })}
            onPageSizeChange={(l) => setParams({ limit: l })}
          />
        )}
      </Card>

      {states.showDeleteModel && (
        <ConfirmationModel
          type="brand"
          show
          onClose={() =>
            handleChangeState({
              showDeleteModel: false,
              selectedBrandId: null,
            })
          }
          onConfirm={handleDeleteBrand}
        />
      )}

      {states.showUpdateModel && (
        <ConfirmationModel
          modelType="update"
          show
          warning={
            states.statusToUpdate === 'inactive'
              ? getStatusWarning('brand')
              : ''
          }
          subTitle={`This action will update the status to ${states.statusToUpdate}`}
          onClose={() =>
            handleChangeState({
              showUpdateModel: false,
              selectedBrandId: null,
              statusToUpdate: null,
            })
          }
          onConfirm={onStatusUpdate}
        />
      )}
    </Layout>
  );
};

export default Brands;
